// Contracts — kids public Remote landing (#53, 2026-07-01). A Remote office has a
// PUBLIC landing page at #/apply/<office> (?ref=<pid> for referral): it renders the
// office's CMS-lite content blocks, a CTA, then a DATA-ONLY form (parent + children,
// NO booking slot, NO ICF) gated by a data-processing consent checkbox. Atlanta
// #/apply stays the booking flow (apply.ts) — the landing is Remote-only. Reuses the
// office_content table (block_key/content) + the intake create shape (no token here;
// the office is in the URL and validated server-side as active + cluster='remote').
import { z } from 'zod';
import { KidsPublicIntakeChildSchema } from './intake';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── GET /api/kids/public/office/:office (public) — landing render payload ─────
// Returns the office identity + its CMS content blocks + compensationEnabled: an
// IMPLICIT optional toggle — true when the manager filled the `compensation` block,
// false when empty (the existing office-content editor is the control). The client
// renders known keys (hero/intro/compensation/…).
export const KidsPublicOfficeBlockSchema = z.object({
  key: z.string(),
  value: z.string(),
});
export type KidsPublicOfficeBlock = z.infer<typeof KidsPublicOfficeBlockSchema>;

// A public consent-block PDF document attached to a moderator's personal landing.
// `url` is the worker-streamed public route (GET /api/kids/public/landing-pdf/:id) —
// never the R2 key (secrets.md). Shown as a download link in the consent block.
export const KidsPublicLandingDocSchema = z.object({
  id: z.string(),
  filename: z.string(),
  url: z.string(),
});
export type KidsPublicLandingDoc = z.infer<typeof KidsPublicLandingDocSchema>;

export const KidsPublicOfficeResSchema = z.object({
  office: z.string(),
  name: z.string(),
  cluster: z.string(), // 'atlanta' | 'remote' — the client shows the landing only for remote
  blocks: z.array(KidsPublicOfficeBlockSchema),
  compensationEnabled: z.boolean(),
  // When the landing was opened with ?ref=<pid> AND that moderator has a personal
  // landing (content override and/or consent PDFs), these carry the moderator-level
  // data: blocks above are already the moderator's (falling back to office),
  // `blockSource` tells the client which won, and docs are the moderator's PDFs.
  blockSource: z.enum(['office', 'moderator']).default('office'),
  docs: z.array(KidsPublicLandingDocSchema).default([]),
});
export type KidsPublicOfficeRes = z.infer<typeof KidsPublicOfficeResSchema>;

// ── POST /api/kids/public/landing-intake (public) — data-only self-registration ─
// office comes from the landing URL (validated active + remote). dataConsent MUST
// be true (z.literal → schema rejects otherwise). refPid = referring moderator's
// 2-digit PID (optional, from ?ref=). No booking day/time (Tim 09.07 — rolled back
// to V2): the landing creates a same-day checked_in meeting so it shows in check-in
// immediately, like the parent intake form. The OTP step is KEPT. No ICF.
export const KidsLandingIntakeReqSchema = z.object({
  office: z.string().trim().min(1).max(60),
  parent: z.object({
    name: z.string().trim().min(1).max(200),
    phone: z.string().trim().max(30).optional(), // optional (Tim 08.07)
    email: z.string().trim().regex(EMAIL_RE).max(200), // REQUIRED (Tim 08.07 — OTP + welcome)
  }),
  // Shares the intake child shape (name/age/gender + optional appearance).
  children: z.array(KidsPublicIntakeChildSchema).min(1).max(20),
  dataConsent: z.literal(true), // data-processing consent — required to submit
  refPid: z.string().trim().regex(/^\d{2}$/).optional(),
  // Email-OTP code (6 digits) — the pre-landing verifies the parent's email
  // before registering (Tim 08.07). The worker checks it against apply_otp.
  code: z.string().trim().regex(/^\d{6}$/),
});
export type KidsLandingIntakeReq = z.infer<typeof KidsLandingIntakeReqSchema>;

export const KidsLandingIntakeResSchema = z.object({ ok: z.literal(true), meetingId: z.string() });
export type KidsLandingIntakeRes = z.infer<typeof KidsLandingIntakeResSchema>;
